import type { SyncStatus } from './status.js';

/**
 * Why the matrix has no rows — five different answers, each an opposite instruction.
 *
 *   never_pulled  no environment has ever reported: go and pull
 *   master_empty  environments hold entities, master holds none: go and adopt
 *   type_empty    the type is genuinely empty everywhere: seed it
 *   filtered      the filters exclude everything: clear them
 *   in_sync       a result, not an absence
 *   studio_empty  the Games page is filtered to a studio master holds no games under: add one
 *
 * Told apart from the stored per-environment counts alone, so this needs nothing the matrix
 * response does not already carry. The rows are master entities, and every master entity
 * classifies as in_sync / drift / missing / excluded in each pulled environment — so if those
 * four are zero everywhere while env_only is not, master has nothing of this type. That is
 * the state a fresh `dev.sh --reset` lands in, and it used to read as "everything is in sync"
 * (or, with the default filter on, "no entities match these filters") over an empty grid.
 */

export const EMPTY_REASONS = [
    'never_pulled',
    'master_empty',
    'type_empty',
    'filtered',
    'in_sync',
    'studio_empty',
] as const;
export type EmptyReason = (typeof EMPTY_REASONS)[number];

/** What an empty grid offers: go and pull, adopt, clear the filters, create one — or nothing. */
export type EmptyAction = 'pull' | 'adopt' | 'clear' | 'create';
/** A title, one short sentence, one action (`config-hub.md` Copy). `namesParent`: the title ends with the studio's inline label, which the component draws. */
export type EmptyCopy = { title: string; namesParent?: true; body: string | null; action: EmptyAction | null };

/** The six empty states' words, by reason; `plural` is the grid's own noun, lower case. */
export const EMPTY_COPY: Record<EmptyReason, (nouns: { plural: string; inParent: boolean }) => EmptyCopy> = {
    never_pulled: () => ({
        title: 'No environment has been pulled yet',
        body: 'Pull an environment to see what it holds.',
        action: 'pull',
    }),
    master_empty: () => ({
        title: 'Master is empty',
        body: 'Adopt from an environment that has records.',
        action: 'adopt',
    }),
    type_empty: ({ plural }) => ({
        title: `No ${plural} anywhere`,
        body: 'Nothing of this type exists in master or any environment yet.',
        action: null,
    }),
    filtered: ({ plural }) => ({
        title: `No ${plural} match these filters`,
        body: 'Try a different search or status.',
        action: 'clear',
    }),
    in_sync: () => ({ title: 'Every studio and game matches master where it is targeted', body: null, action: null }),
    studio_empty: ({ plural, inParent }) => ({
        ...(inParent ? { title: `No ${plural} in`, namesParent: true as const } : { title: `No ${plural} in master` }),
        body: 'Add one, or adopt them from an environment.',
        action: 'create',
    }),
};

type Counts = Record<SyncStatus, number>;

/** What this module needs of a matrix column — `components/Matrix/types.ts`'s `MatrixColumn` satisfies it structurally. */
export type CountsColumn = { name: string; counts: Counts | null; countsByType: Record<string, Counts | undefined> };

const pulled = (columns: CountsColumn[]): Counts[] =>
    columns.map((column) => column.counts).filter((counts): counts is Counts => counts !== null);

const masterKnown = (counts: Counts): number => counts.in_sync + counts.drift + counts.missing + counts.excluded;

/**
 * `studio` is the Games page's studio filter. The grid lists master only, so an unfiltered empty
 * list under a studio means master holds no games there — whatever the environments report.
 */
export const emptyReason = (columns: CountsColumn[], filtered: boolean, studio: string | null = null): EmptyReason => {
    if (studio) return filtered ? 'filtered' : 'studio_empty';

    const reported = pulled(columns);
    if (columns.length > 0 && reported.length === 0) return 'never_pulled';

    if (reported.length > 0 && reported.every((counts) => masterKnown(counts) === 0)) {
        return reported.some((counts) => counts.env_only > 0) ? 'master_empty' : 'type_empty';
    }

    return filtered ? 'filtered' : 'in_sync';
};

/** The pulled environment holding the most env_only entities — the natural `adopt --from`. */
export const suggestedAdoptSource = (columns: CountsColumn[]): string | null => {
    let best: { name: string; envOnly: number } | null = null;
    for (const column of columns) {
        if (column.counts === null) continue;
        if (best === null || column.counts.env_only > best.envOnly) {
            best = { name: column.name, envOnly: column.counts.env_only };
        }
    }
    return best?.name ?? null;
};

/**
 * How many master records of one type the environments last counted: every master record
 * classifies as in_sync / drift / missing / excluded in each pulled environment, so any pulled
 * environment's per-type counts sum to it. Null while nothing has been pulled.
 */
export const masterCount = (columns: CountsColumn[], type: string): number | null => {
    for (const column of columns) {
        if (column.counts === null) continue;
        const counts = column.countsByType[type];
        return counts ? masterKnown(counts) : 0;
    }
    return null;
};

/**
 * How many master records a list page stands for.
 *
 * Exact when every row is loaded and no row filter narrows them; otherwise `known` (the pull
 * record's count, from `masterCount`), which is what the list would reach by paging. Null when
 * neither is known.
 */
export const listTotal = ({
    loaded,
    hasMore,
    filtered,
    known,
}: {
    loaded: number;
    hasMore: boolean;
    filtered: boolean;
    known: number | null;
}): number | null => (!hasMore && !filtered ? loaded : known);
